import { observer } from "mobx-react-lite";
import { useLocalization } from "@progress/kendo-react-intl";
import { useStores } from "src/stores";
import { ExcelExport, ExcelExportColumn } from '@progress/kendo-react-excel-export';
import { useEffect, useState, useRef } from "react";
import { useServices } from "src/services";
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridDataStateChangeEvent,
  GridToolbar
} from "@progress/kendo-react-grid";
import { Button, ToolbarSpacer } from "@progress/kendo-react-buttons";
import { useHistory } from "react-router";
import { DataResult, process, State } from "@progress/kendo-data-query";
import { D4URxEvent } from "src/utils/classes";
import {
  ColumnMenu,
} from "src/components/GridColumnMenus";
import multiColumnSort from "multi-column-sort";
import moment from 'moment-timezone';
import { DatePicker } from 'src/components/Form/DatePicker';

interface ExportColumnDef {
  key: string;
  field: string;
  title: string;
}

const KitDeliveryScreen = () => {
  const localizationService = useLocalization();

  const _export = useRef<ExcelExport | null>(null);
  const excelExport = () => {
    if (_export.current !== null) {
      _export.current.save();
    }
  };

  const exportColumnDefs: ExportColumnDef[] = [
    { key: 'kit_ship_date', field: 'kit_ship_date', title: 'Ship Date' },
    { key: 'kit_tracking_number', field: 'kit_tracking_number', title: 'Tracking Number' },
    { key: 'secondary_tracking_number', field: 'secondary_tracking_number', title: 'Secondary Tracking Number' },
    { key: 'event_id', field: 'event_id', title: 'Latitude Event ID' },
    { key: 'start_date', field: 'start_date', title: 'Event Date' },
    { key: 'shipping_location', field: 'shipping_location', title: 'Shipping Location' },
    { key: 'dealer_name', field: 'dealer_name', title: 'Dealer' },
    { key: 'hispanic', field: 'hispanic', title: 'Hispanic Dealer' },
    { key: 'dealer_contact_name', field: 'dealer_contact_name', title: 'Dealer Contact' },
    { key: 'dealer_contact_email', field: 'dealer_contact_email', title: 'Dealer Contact Email' },
    { key: 'dealer_contact_phone', field: 'dealer_contact_phone', title: 'Dealer Contact Phone' },
    { key: 'address', field: 'address', title: 'Delivery Address' },
    { key: 'address_2', field: 'address_2', title: 'Address 2' },
    { key: 'city', field: 'city', title: 'City' },
    { key: 'state', field: 'state', title: 'State' },
    { key: 'zip_code', field: 'zip_code', title: 'Zip Code' },
    { key: 'attn_contact_name', field: 'attn_contact_name', title: 'Attn. Contact Name' },
    { key: 'attn_contact_email', field: 'attn_contact_email', title: 'Attn. Contact Email' },
    { key: 'attn_contact_phone', field: 'attn_contact_phone', title: 'Attn. Contact Phone' },
    { key: 'gets_spanish_kit', field: 'gets_spanish_kit', title: 'Gets Spanish Kit' },
  ];

  const [selectedExportColumns, setSelectedExportColumns] = useState<string[]>(
    exportColumnDefs.map((col) => col.key)
  );
  const toggleExportColumn = (key: string) => {
    setSelectedExportColumns((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };
  const selectAllExportColumns = () => setSelectedExportColumns(exportColumnDefs.map((col) => col.key));
  const clearAllExportColumns = () => setSelectedExportColumns([]);
  const selectedExportColumnDefs = exportColumnDefs.filter((col) => selectedExportColumns.includes(col.key));

  const gridContentRef = useRef<HTMLDivElement | null>(null);
  const topScrollbarRef = useRef<HTMLDivElement | null>(null);
  const [scrollbarWidth, setScrollbarWidth] = useState<number>(3000);

  const { ui, login } = useStores();
  const { d4ur } = useServices();
  let history = useHistory();

  const sortPresets: { [key: string]: State; } = {
    clear: {
      sort: [
        {
          field: 'sort_date',
          dir: 'asc',
        },
      ]
    },
    default: {
      filter: {
        logic: "or", filters: [
          // No tracking number yet
          {
            field: "kit_tracking_number",
            operator: "eq",
            value: ''
          },
          {
            field: "kit_tracking_number",
            operator: "isnull",
          },
          // Tracking number may exist, but ship date not set yet
          {
            field: "kit_ship_date",
            operator: "isnull",
          }
        ]
      },
      sort: [
        {
          field: 'start_date',
          dir: 'asc',
        },
      ]
    },
  }

  const [dataState, setDataState] = useState<State>(sortPresets.default);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');

  type Direction = "ASC" | "DESC";
  type SortObject<T> = {
    [key in keyof T]?: Direction;
  };

  const customProcess = (data: any[], state: State) => {
    const preProcessed = process(data, state).data;
    // custom sort
    const getColumnValues = (column: any, value: any) => {
      switch (column) {
        // case 'sort_status':
        //     return value.data.value;
        default:
          return value;
      }
    }

    const sortOrder: SortObject<any> = {};

    for (const s of (state.sort || [])) {
      let fieldToSort = s.field;

      switch (fieldToSort) {
        case 'status_pretty':
          fieldToSort = 'sort_status';
          break;
      }

      sortOrder[fieldToSort] = s.dir?.toUpperCase() as Direction;
    };

    let newData = [];
    if (state.sort?.length) {
      newData = multiColumnSort(
        preProcessed,
        sortOrder,
        getColumnValues
      );
    } else {
      newData = preProcessed;
    }

    return {
      data: newData,
      total: preProcessed.length,
    } as DataResult;
  }


  const processEvents = (events: D4URxEvent[]): D4URxEvent[] => {
    return events.map((e) => {
      return new D4URxEvent(e);
    });
  };

  // const [planners, setPlanners] = useState<UserDetails[]>([]);
  const [eventList, setEventList] = useState<D4URxEvent[]>([]);
  const [dataResult, setDataResult] = useState<DataResult>(
    customProcess(eventList, dataState)
  );

  const loadEvents = () => {
    ui.setIsGettingEvents(true);
    d4ur.getRRDData(ui.currentSeason.season_id).then((ret) => {
      console.log('get rrd data ret :', ret);
      ui.setIsGettingEvents(false);

      const processedEvents = processEvents(
        (ret as D4URxFetchResult).result || []
      );

      // loop through processed events and move custom_data.kit_ship_date to the top level for excel export
      processedEvents.forEach((e) => {
        const { custom_data } = e;
        const kitShipDate = JSON.parse(custom_data || '').kit_ship_date || null;
        e.kit_ship_date = kitShipDate ? moment(kitShipDate).format('YYYY-MM-DD') : null;
      });

      setEventList(processedEvents);
      setDataResult(customProcess(processedEvents, dataState));
      // const rdaTemp = processedEvents.filter((e) => e.event_id && !e.cancelled);
    });
  }

  useEffect(() => {
    loadEvents();
  }, []);

  useEffect(() => {
    if (eventList.length === 0) return;
    const measureWidth = () => {
      const gridContent = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement;
      if (gridContent && gridContent.scrollWidth > 0) setScrollbarWidth(gridContent.scrollWidth);
    };
    const syncTopScrollbar = () => {
      const gridContent = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement;
      if (gridContent && topScrollbarRef.current) topScrollbarRef.current.scrollLeft = gridContent.scrollLeft;
    };
    requestAnimationFrame(() => {
      measureWidth();
      setTimeout(measureWidth, 200);
      const gridContent = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement;
      if (gridContent) gridContent.addEventListener('scroll', syncTopScrollbar);
    });
    window.addEventListener('resize', measureWidth);
    return () => {
      window.removeEventListener('resize', measureWidth);
      const gridContent = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement;
      if (gridContent) gridContent.removeEventListener('scroll', syncTopScrollbar);
    };
  }, [eventList.length, dataResult.data.length]);

  useEffect(() => {
    if (eventList.length > 0) {
      console.log('Kit Delivery events:', eventList.length, 'events', eventList);
      eventList.forEach((event, i) => {
        console.log(`Kit Delivery event [${i}] properties:`, {
          event_id: event.event_id,
          event_name: event.event_name,
          kit_tracking_number: event.kit_tracking_number,
          secondary_tracking_number: event.secondary_tracking_number,
          kit_ship_date: event.kit_ship_date,
          dealer_name: event.dealer_name,
          shipping_location: event.shipping_location,
        });
      });
    }
  }, [eventList]);

  const trackingNumberCell = (props: GridCellProps) => {
    const { event_id, kit_tracking_number } = new D4URxEvent(props.dataItem);
    console.log('kit_tracking_number :', typeof kit_tracking_number, kit_tracking_number);

    const [trackingNumber, setTrackingNumber] = useState<string>(kit_tracking_number ? kit_tracking_number : '');

    return (
      <td style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-evenly', alignItems: 'center' }}>
        <input
          placeholder="Tracking #"
          value={trackingNumber}
          onChange={(e) => setTrackingNumber(e.target.value)}
        />
        <Button
          className="k-button k-primary"
          style={{ marginTop: '5px' }}
          onClick={async () => {
            if (event_id) {
              try {
                await d4ur.updateTracking(event_id, trackingNumber);
                loadEvents();
                setSuccessMessage('Tracking number saved successfully.');
                setTimeout(() => {
                  setSuccessMessage('');
                }, 2500);
              } catch (error: any) {
                alert(`There was an error saving the tracking number (${error.message ? error.message : 'unknown error'})`);
              }
            }
          }}
        >
          Save
        </Button>
        {kit_tracking_number && <Button
          className="k-button k-primary"
          style={{ marginTop: '5px' }}
          onClick={async () => {
            window.open(`https://www.fedex.com/fedextrack/?trknbr=${kit_tracking_number}`, '_blank');
          }}
        >
          Go to Tracking
        </Button>}
      </td>
    );
  };

  const secondaryTrackingNumberCell = (props: GridCellProps) => {
    const { event_id, secondary_tracking_number } = new D4URxEvent(props.dataItem);
    const [trackingNumber, setTrackingNumber] = useState<string>(secondary_tracking_number ?? '');

    return (
      <td style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-evenly', alignItems: 'center' }}>
        <input
          placeholder="Secondary Tracking #"
          value={trackingNumber}
          onChange={(e) => setTrackingNumber(e.target.value)}
        />
        <Button
          className="k-button k-primary"
          style={{ marginTop: '5px' }}
          onClick={async () => {
            if (event_id != null) {
              try {
                await d4ur.updateSecondaryTracking(event_id, trackingNumber);
                loadEvents();
                setSuccessMessage('Secondary tracking number saved successfully.');
                setTimeout(() => setSuccessMessage(''), 2500);
              } catch (error: any) {
                alert(`There was an error saving the secondary tracking number (${error.message ?? 'unknown error'})`);
              }
            }
          }}
        >
          Save
        </Button>
        {secondary_tracking_number && (
          <Button
            className="k-button k-primary"
            style={{ marginTop: '5px' }}
            onClick={() => {
              window.open(`https://www.fedex.com/fedextrack/?trknbr=${secondary_tracking_number}`, '_blank');
            }}
          >
            Go to Tracking
          </Button>
        )}
      </td>
    );
  };

  const shipDateCell = (props: GridCellProps) => {
    const { event_id, kit_tracking_number, custom_data } = new D4URxEvent(props.dataItem);
    const kitShipDate = JSON.parse(custom_data || '').kit_ship_date || null;
    if (kit_tracking_number) {
      return (
        <td >
          <DatePicker
            value={kitShipDate ? new Date(kitShipDate) : null}
            onChange={async (e: any) => {
              if (event_id) {
                try {
                  await d4ur.updateShipDate(event_id, e.value);
                  loadEvents();
                  setSuccessMessage('Ship date saved successfully.');
                  setTimeout(() => {
                    setSuccessMessage('');
                  }, 2500);
                } catch (error: any) {
                  alert(`There was an error saving the ship date (${error.message ? error.message : 'unknown error'})`);
                }
              }
            }}
          />
        </td>
      )
    }
    return (
      <td >
        <p>Not Shipped</p>
      </td>
    )
  };

  const resendEmailCell = (props: GridCellProps) => {
    const { event_id, kit_tracking_number, custom_data } = new D4URxEvent(props.dataItem);
    const kitEmailData = JSON.parse(custom_data || '').kit_delivery_email || []
    const getLastSentAt = (emailsSent: Array<emailsSent>) => {
      console.log('emailsSent :', emailsSent);
      if (!emailsSent || !emailsSent.length) return 'Not Yet Sent';
      const parseDate = (email: emailsSent) => new Date(email.sent_at);
      emailsSent.sort((a, b) => parseDate(b).getTime() - parseDate(a).getTime());
      const mostRecentEmail = emailsSent[0];
      // return `${moment(mostRecentEmail.sent_at).tz('America/New_York').format('MMMM Do, h:mm a')} (EST) to ${mostRecentEmail.sent_to.length > 1 ? mostRecentEmail.sent_to.join(', ') : mostRecentEmail.sent_to}`;
      return `Last Sent: ${moment(mostRecentEmail.sent_at).tz('America/New_York').format('MMMM Do, h:mm a')} (EST)`;
    }
    return (
      <td>
        <Button
          className="k-button k-primary"
          style={{ marginTop: '5px' }}
          onClick={async () => {
            if (event_id) {
              try {
                await d4ur.triggerEmail('kit_delivery', event_id, '');
                loadEvents();
                setSuccessMessage('Email sent successfully.');
                setTimeout(() => {
                  setSuccessMessage('');
                }, 2500);
              } catch (error: any) {
                alert(`There was an error sending the email (${error.message ? error.message : 'unknown error'})`);
              }
            }
          }}
          disabled={!kit_tracking_number}
        >
          Re-Send Dealer Kit Tracking Email
        </Button>
        {kit_tracking_number ? <p>{getLastSentAt(kitEmailData)}</p> : null}
      </td>
    )
  };

  const spanishKitCell = (props: GridCellProps) => {
    const { event_id, gets_spanish_kit } = new D4URxEvent(props.dataItem);
    
    // Parse custom data to check for spanish kit preference
    const getsSpanishKit = gets_spanish_kit;

    const handleSpanishKitChange = (e: any) => {
      return d4ur
        .updateSpanishKit(event_id, e.target.checked ? 1 : 0)
        .then(() => {
          // This forces a reload of the entire dataset from the server
          loadEvents();
        });
    };
    
    return (
      <td style={{ textAlign: 'center' }}>
        <input
          type="checkbox"
          checked={getsSpanishKit}
          onChange={handleSpanishKitChange}
          style={{ width: "20px", height: "20px", cursor: "pointer" }}
        />
      </td>
    );
  };

  // const spanishKitCell = (props: GridCellProps) => {
  //   const { event_id, gets_spanish_kit } = props.dataItem;
    
  //   // Check if gets_spanish_kit is already a boolean
  //   const isChecked = gets_spanish_kit === true || gets_spanish_kit === 'YES';
  
  //   return (
  //     <td style={{ textAlign: 'center' }}>
  //       <input
  //         type="checkbox"
  //         checked={isChecked}
  //         onChange={(e) => {
  //           // Just call the API directly when checked/unchecked
  //           if (event_id) {
  //             d4ur.updateSpanishKit(event_id, e.target.checked ? 1 : 0);
  //           }
  //         }}
  //         style={{ width: "20px", height: "20px", cursor: "pointer" }}
  //       />
  //     </td>
  //   );
  // };

  const doDataState = (state: State) => {
    console.log('event.dataState', state);
    setDataResult(customProcess(eventList, state));
    setDataState(state);
  }

  const FormatExportData = (data: any[]) => {
    return data.map((d: any) => {
      return {
        ...d,
        first_time_dealer: d.first_time_dealer ? 'YES' : null,
        hispanic: d.hispanic ? 'YES' : null,
        gets_spanish_kit: d.gets_spanish_kit ? 'YES' : null,
      };
    });
  };
  return (
    <div id="Dashboard" className="dashboard-page main-content">
      <div className="card-container grid">
        <div>
          <p>User: {login.user?.credentials.display_name}</p>
          <h3 className="card-title">
            {localizationService.toLanguageString("custom.kit_delivery", "RRD Kit Delivery Information")}
          </h3>
          <p>Spanish Kits Needed: {eventList.filter(event => event.gets_spanish_kit).length}</p>
          {successMessage ? <p style={{ color: 'green' }}>{successMessage}</p> : null}
        </div>
        {/* // TODO: going to replace "sending..." with real spinner during styling updates. same on Event page.  */}
        {isLoading ? <p style={{ color: 'red' }}>Sending...</p> : null}
        {errorMessage ? <p style={{ color: 'red' }}>{errorMessage}</p> : null}
        <div className="card-component">
          <ExcelExport data={FormatExportData(dataResult.data)} ref={_export} fileName={`rrd_kit_delivery_export_${moment().format('YYYYMMDD-hhmma')}.xlsx`}>
            {selectedExportColumnDefs.map((col) => (
              <ExcelExportColumn key={col.key} field={col.field} title={col.title} />
            ))}
          </ExcelExport>
          <div style={{ marginBottom: '12px' }}>
            <details>
              <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                Choose Excel columns ({selectedExportColumnDefs.length} selected)
              </summary>
              <div style={{ marginTop: '8px' }}>
                <button className="k-button k-button-md k-rounded-md" onClick={selectAllExportColumns} type="button" style={{ marginRight: '8px' }}>Select All</button>
                <button className="k-button k-button-md k-rounded-md" onClick={clearAllExportColumns} type="button">Clear All</button>
              </div>
              <div style={{ marginTop: '10px', display: 'grid', gridTemplateColumns: 'repeat(3, minmax(220px, 1fr))', gap: '6px 12px' }}>
                {exportColumnDefs.map((col) => (
                  <label key={col.key} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input type="checkbox" checked={selectedExportColumns.includes(col.key)} onChange={() => toggleExportColumn(col.key)} />
                    <span>{col.title}</span>
                  </label>
                ))}
              </div>
            </details>
          </div>

          <style>{`
            .kitdelivery-grid-wrapper .k-grid-content { overflow-x: scroll; scrollbar-width: none; -ms-overflow-style: none; }
            .kitdelivery-grid-wrapper .k-grid-content::-webkit-scrollbar { display: none; }
            .kitdelivery-top-scrollbar { overflow-x: scroll; overflow-y: hidden; height: 14px; position: sticky; top: 0; z-index: 50; background: #f5f5f5; border-bottom: 1px solid #ddd; }
            .kitdelivery-top-scrollbar::-webkit-scrollbar { height: 8px; }
            .kitdelivery-top-scrollbar::-webkit-scrollbar-track { background: #f1f1f1; }
            .kitdelivery-top-scrollbar::-webkit-scrollbar-thumb { background: #aaa; border-radius: 4px; }
            .kitdelivery-top-scrollbar::-webkit-scrollbar-thumb:hover { background: #666; }
          `}</style>
          <div style={{ height: '70vh', overflowY: 'auto', overflowX: 'hidden' }}>
            <div ref={topScrollbarRef} className="kitdelivery-top-scrollbar" onScroll={(e) => { const gc = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement; if (gc) gc.scrollLeft = e.currentTarget.scrollLeft; }}>
              <div style={{ width: scrollbarWidth, height: 1 }} />
            </div>
            <div ref={gridContentRef} className="kitdelivery-grid-wrapper">
            <Grid
            data={dataResult}
            sortable={{ mode: "single" }}
            {...dataState}
            onDataStateChange={(event: GridDataStateChangeEvent) => {
              console.log("event.dataState", event.dataState);
              setDataResult(customProcess(eventList, event.dataState));
              setDataState(event.dataState);
            }}
            resizable={true}
            onColumnResize={() => { const gc = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement; if (gc) setScrollbarWidth(gc.scrollWidth); }}
          >
            <GridToolbar>
              {/* <button className="k-button k-button-md k-rounded-md k-button-solid" onClick={() => doDataState(sortPresets.default)}>Default</button>
              <button className="k-button k-button-md k-rounded-md k-button-solid" onClick={() => doDataState(sortPresets.action)}>Action Needed</button>
              <button className="k-button k-button-md k-rounded-md k-button-solid" onClick={() => doDataState(sortPresets.waiting)}>Waiting on Dealer</button>
              <button className="k-button k-button-md k-rounded-md k-button-solid" onClick={() => doDataState(sortPresets.today)}>Today's Events</button>
              <button className="k-button k-button-md k-rounded-md k-button-solid" onClick={() => doDataState(sortPresets.tomorrow)}>Tomorrow</button>
              <button className="k-button k-button-md k-rounded-md k-button-solid" onClick={() => doDataState(sortPresets.thisWeek)}>This Week</button>
              <button className="k-button k-button-md k-rounded-md k-button-solid" onClick={() => doDataState(sortPresets.nextWeek)}>Next Week</button>
              <button className="k-button k-button-md k-rounded-md k-button-solid" onClick={() => doDataState(sortPresets.yesterday)}>Yesterday</button>
              <button className="k-button k-button-md k-rounded-md k-button-solid" onClick={() => doDataState(sortPresets.lastWeek)}>Last Week</button> */}
              <button className="k-button k-button-md k-rounded-md k-button-solid" onClick={() => doDataState(sortPresets.default)}>Default</button>
              <button className="k-button k-button-md k-rounded-md k-button-solid" onClick={() => doDataState(sortPresets.clear)}>Show All Events</button>
              <ToolbarSpacer />
              <button
                className="k-button k-button-md k-rounded-md k-button-solid"
                onClick={excelExport}
                disabled={selectedExportColumnDefs.length === 0}
              >
                Export Current View ({selectedExportColumnDefs.length} columns)
              </button>
            </GridToolbar>
            {/* <GridColumn
            field="event_name"
            title={localizationService.toLanguageString(
                "custom.event_name",
                "Event Name"
            )}
            columnMenu={ColumnMenu}
            /> */}
            <GridColumn key="ship-date" title='Ship Date' cell={shipDateCell} width={200} />
            <GridColumn key="tracking" title='Tracking #' cell={trackingNumberCell} width={200} />
            <GridColumn
              field="dealer_name"
              title={localizationService.toLanguageString(
                "custom.dealer",
                "dealer"
              )}
              columnMenu={ColumnMenu}
              width={100}
            />
            <GridColumn
              field="hispanicReadable"
              title="Hispanic"
              columnMenu={ColumnMenu}
              width={100}
            />
            <GridColumn
              field="start_date"
              title="Event Date"
              format="{0:E, MMM dd yyyy}"
              filter="date"
              columnMenu={ColumnMenu}
              width={150}
            />
            <GridColumn
              field="shipping_location"
              title={localizationService.toLanguageString(
                "custom.shipping_location",
                "Shipping Location"
              )}
              columnMenu={ColumnMenu}
              width={200}
            />
            {/* <GridColumn
              field="dealer_contact_name"
              title={localizationService.toLanguageString(
                "custom.dealer_contact",
                "Dealer Contact Name"
              )}
              columnMenu={ColumnMenu}
              cell={DealerNameCell}
            />
            <GridColumn
              field="dealer_contact_email"
              title={localizationService.toLanguageString(
                "custom.dealer_contact",
                "Dealer Contact Email"
              )}
              columnMenu={ColumnMenu}
              cell={DealerNameCell}
            />
            <GridColumn
              field="dealer_contact_phone"
              title={localizationService.toLanguageString(
                  "custom.dealer_contact",
                  "Dealer Contact Phone"
                )}
                columnMenu={ColumnMenu}
                cell={DealerNameCell}
                />
                <GridColumn
                field="charity_partner"
                title="Organization"
                columnMenu={ColumnMenu}
            /> */}
            <GridColumn
              field="attn_contact_name"
              title="Attn. Name"
              columnMenu={ColumnMenu}
              width={150}
            />
            <GridColumn
              field="address"
              title="Address"
              columnMenu={ColumnMenu}
              width={150}
            />
            <GridColumn
              field="address_2"
              title="Address 2"
              columnMenu={ColumnMenu}
              width={150}
            />
            <GridColumn
              field="city"
              title="City"
              columnMenu={ColumnMenu}
            />
            <GridColumn
              field="state"
              title="State"
              columnMenu={ColumnMenu}
              width={100}
            />
            <GridColumn
              field="zip_code"
              title="Zip Code"
              columnMenu={ColumnMenu}
              width={150}
            />
            <GridColumn title='Resend Email' cell={resendEmailCell} width={300} />
            <GridColumn title='Gets Spanish Kit' cell={spanishKitCell} width={150} />
            <GridColumn key="secondary-tracking" field='secondary_tracking_number' title='Secondary Tracking #' cell={secondaryTrackingNumberCell} width={200} />
            {/* <GridColumn
              field="attn_contact_email"
              title="Attn. Email"
              columnMenu={ColumnMenu}
            />
            <GridColumn
              field="attn_contact_phone"
              title="Attn. Phone"
              columnMenu={ColumnMenu}
            /> */}

            </Grid>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};



export default observer(KitDeliveryScreen);
